/**
 * @fileoverview Color system constants
 *
 * Every value resolves to a CSS variable written by src/theme/cssVariables.js,
 * so the kit follows the entreprise brand instead of a hardcoded palette.
 *
 * @module ui/shared/utils/colors
 */
import { buildCssVariables } from "@/theme";

/**
 * The active profile's value for each variable, used as the `var()` fallback so
 * these can never drift from the design profile the way a literal hex would.
 */
const profileDefaults: Record<string, string> = buildCssVariables(null);

const step = (name: string) => `var(${name}, ${profileDefaults[name]})`;

export const colors = {
  primary: {
    50: step("--color-primary-50"),
    100: step("--color-primary-100"),
    200: step("--color-primary-200"),
    300: step("--color-primary-300"),
    400: step("--color-primary-400"),
    500: step("--color-primary-500"),
    600: step("--color-primary-600"),
    700: step("--color-primary-700"),
    800: step("--color-primary-800"),
    900: step("--color-primary-900"),
  },
  gray: {
    50: step("--gray-50"),
    100: step("--gray-100"),
    200: step("--gray-200"),
    300: step("--gray-300"),
    400: step("--gray-400"),
    500: step("--gray-500"),
    600: step("--gray-600"),
    700: step("--gray-700"),
    800: step("--gray-800"),
    900: step("--gray-900"),
  },
  success: {
    50: step("--success-green-bg"),
    100: step("--success-green-bg"),
    200: step("--success-green-border"),
    300: step("--success-green-border"),
    400: step("--success-green"),
    500: step("--success-green"),
    600: step("--success-green"),
    700: step("--success-green"),
    800: step("--success-green"),
    900: step("--success-green"),
  },
  warning: {
    50: step("--warning-bg"),
    100: step("--warning-bg"),
    200: step("--warning-border"),
    300: step("--warning-border"),
    400: step("--warning-color"),
    500: step("--warning-color"),
    600: step("--warning-orange"),
    700: step("--warning-orange"),
    800: step("--warning-orange"),
    900: step("--warning-orange"),
  },
  danger: {
    50: step("--error-bg"),
    100: step("--error-bg"),
    200: step("--error-border"),
    300: step("--error-border"),
    400: step("--error-color"),
    500: step("--error-color"),
    600: step("--error-color"),
    700: step("--error-color"),
    800: step("--error-color"),
    900: step("--error-color"),
  },
} as const;

export const semanticColors = {
  text: {
    primary: step("--text-heading"),
    secondary: step("--text-body"),
    muted: step("--text-muted"),
    disabled: step("--gray-500"),
  },
  background: {
    default: step("--surface-card"),
    muted: step("--bg-color"),
    disabled: step("--gray-100"),
  },
  border: {
    default: step("--border-color"),
    focus: step("--primary-color"),
    error: step("--error-color"),
  },
} as const;
